// components/team/OwnershipRequestsModal.tsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getAuthContext } from '../../utils/jwt';
import { FiCheck, FiX } from 'react-icons/fi';
import { BASE_URL } from '../../utils/constants';

interface OwnershipRequest {
    id: string;
    tenantId: string;
    requestedByUserId: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    createdAt: string;
    updatedAt: string;
}

interface OwnershipRequestsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onActionComplete?: () => void;
}

const FiXComponent = FiX as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;

const FiCheckComponent = FiCheck as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
}>;

const OwnershipRequestsModal: React.FC<OwnershipRequestsModalProps> = ({
    isOpen,
    onClose,
    onActionComplete,
}) => {
    const [requests, setRequests] = useState<OwnershipRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const { token, tenantId, userId } = getAuthContext();

            if (!token || !tenantId) {
                toast.error('Authentication required');
                return;
            }

            const response = await fetch(`${BASE_URL}/v1/team/ownership/requests`, {
                method: 'GET',
                headers: {
                    'X-Tenant-Id': tenantId,
                    'X-User-Id': userId || '',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch ownership requests: ${response.status}`);
            }

            const data = await response.json();
            setRequests(Array.isArray(data) ? data : []);
        } catch (error: any) {
            toast.error(error.message || 'Failed to fetch ownership requests');
            // console.error('Fetch requests error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (requestId: string) => {
        try {
            setProcessingIds(prev => new Set(prev).add(requestId));
            const { token, tenantId, userId } = getAuthContext();

            if (!token || !tenantId) {
                toast.error('Authentication required');
                return;
            }

            const loadingToast = toast.loading('Approving request...');

            const response = await fetch(`${BASE_URL}/v1/team/ownership/requests/${requestId}/approve`, {
                method: 'POST',
                headers: {
                    'X-Tenant-Id': tenantId,
                    'X-User-Id': userId || '',
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            toast.dismiss(loadingToast);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to approve request: ${response.status}`);
            }

            toast.success('Ownership request approved successfully!');

            // Close modal and reload page
            onClose();
            if (onActionComplete) {
                onActionComplete();
            }

        } catch (error: any) {
            toast.error(error.message || 'Failed to approve request');
            // console.error('Approve request error:', error);
        } finally {
            setProcessingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(requestId);
                return newSet;
            });
        }
    };

    const handleReject = async (requestId: string) => {
        try {
            setProcessingIds(prev => new Set(prev).add(requestId));
            const { token, tenantId, userId } = getAuthContext();

            if (!token || !tenantId) {
                toast.error('Authentication required');
                return;
            }

            const loadingToast = toast.loading('Rejecting request...');

            // Using requestId in the URL path
            const response = await fetch(`${BASE_URL}/v1/team/ownership/requests/${requestId}/reject`, {
                method: 'POST',
                headers: {
                    'X-Tenant-Id': tenantId,
                    'X-User-Id': userId || '',
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            toast.dismiss(loadingToast);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to reject request: ${response.status}`);
            }

            toast.success('Ownership request rejected successfully!');

            // Close modal and reload page
            onClose();
            if (onActionComplete) {
                onActionComplete();
            }

        } catch (error: any) {
            toast.error(error.message || 'Failed to reject request');
            // console.error('Reject request error:', error);
        } finally {
            setProcessingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(requestId);
                return newSet;
            });
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchRequests();
        }
    }, [isOpen]);

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getStatusBadge = (status: string) => {
        const statusMap: Record<string, { color: string; label: string }> = {
            PENDING: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
            APPROVED: { color: 'bg-green-100 text-green-800', label: 'Approved' },
            REJECTED: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
        };
        const { color, label } = statusMap[status] || statusMap.PENDING;
        return <span className={`px-3 py-1 rounded-full text-sm ${color}`}>{label}</span>;
    };

    const pendingRequests = requests.filter(req => req.status === 'PENDING');
    const completedRequests = requests.filter(req => req.status !== 'PENDING');

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[99999] flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b">
                    <h2 className="text-xl font-semibold text-gray-800">Ownership Requests</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <FiXComponent className="w-6 h-6" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00A4BD]"></div>
                            <span className="ml-3 text-gray-500">Loading requests...</span>
                        </div>
                    ) : requests.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16">
                            <div className="bg-gray-50 rounded-full p-4 mb-4">
                                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No requests found</h3>
                            <p className="text-gray-500 text-center max-w-sm">
                                There are no pending or completed ownership requests at this time.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Pending Requests */}
                            {pendingRequests.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-500 mb-3">Pending Requests</h3>
                                    <div className="border rounded-lg overflow-hidden">
                                        <table className="w-full text-left">
                                            <thead className="bg-gray-50">
                                                <tr className="text-gray-600 text-sm">
                                                    <th className="px-4 py-3 font-normal">Request ID</th>
                                                    <th className="px-4 py-3 font-normal">User ID</th>
                                                    <th className="px-4 py-3 font-normal">Requested At</th>
                                                    <th className="px-4 py-3 font-normal">Status</th>
                                                    <th className="px-4 py-3 font-normal text-center">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {pendingRequests.map((request) => (
                                                    <tr key={request.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 text-sm font-mono text-gray-600">
                                                            {request.id.substring(0, 12)}...
                                                        </td>
                                                        <td className="px-4 py-3 text-sm font-mono text-gray-600">
                                                            {request.requestedByUserId.substring(0, 12)}...
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            {formatDate(request.createdAt)}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {getStatusBadge(request.status)}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-center justify-center gap-3">
                                                                <div className="relative group">
                                                                    <button
                                                                        onClick={() => handleApprove(request.id)}
                                                                        disabled={processingIds.has(request.id)}
                                                                        className="p-1.5 bg-green-100 text-green-600 rounded hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    >
                                                                        <FiCheckComponent className="w-5 h-5" />
                                                                    </button>
                                                                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap">
                                                                        Approve
                                                                    </span>
                                                                </div>
                                                                <div className="relative group">
                                                                    <button
                                                                        onClick={() => handleReject(request.id)}
                                                                        disabled={processingIds.has(request.id)}
                                                                        className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    >
                                                                        <FiXComponent className="w-5 h-5" />
                                                                    </button>
                                                                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap">
                                                                        Reject
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Completed Requests */}
                            {completedRequests.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-500 mb-3">Completed Requests</h3>
                                    <div className="border rounded-lg overflow-hidden">
                                        <table className="w-full text-left">
                                            <thead className="bg-gray-50">
                                                <tr className="text-gray-600 text-sm">
                                                    <th className="px-4 py-3 font-normal">Request ID</th>
                                                    <th className="px-4 py-3 font-normal">User ID</th>
                                                    <th className="px-4 py-3 font-normal">Requested At</th>
                                                    <th className="px-4 py-3 font-normal">Status</th>
                                                    <th className="px-4 py-3 font-normal">Processed At</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {completedRequests.map((request) => (
                                                    <tr key={request.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3 text-sm font-mono text-gray-600">
                                                            {request.id.substring(0, 12)}...
                                                        </td>
                                                        <td className="px-4 py-3 text-sm font-mono text-gray-600">
                                                            {request.requestedByUserId.substring(0, 12)}...
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            {formatDate(request.createdAt)}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {getStatusBadge(request.status)}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            {formatDate(request.updatedAt)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OwnershipRequestsModal;